import { FC, useCallback, useEffect, useState } from 'react';

import {
  CheckCircleIcon,
  ExclamationIcon,
  LockClosedIcon,
  RefreshIcon,
  ShieldCheckIcon,
} from '@heroicons/react/outline';
import { DuplicateRecipientConflict } from '@service/giftStore/utils/kakaoDuplicateRecipient';
import { ShareChannel } from '@shared/api/giftCoupon/giftCoupon.interface';
import { Icon } from '@shared/components/icons';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';

import { SeqChip } from './SeqChip';

const CHANNEL_LABEL: Record<ShareChannel, string> = {
  [ShareChannel.KakaoTalk]: '카카오톡',
  [ShareChannel.SMS]: '문자',
  [ShareChannel.Link]: '다른 앱',
  [ShareChannel.Other]: '다른 앱',
};

const formatSharedAt = (date: Date | string) => {
  const d = new Date(date);
  return `${d.getMonth() + 1}월 ${d.getDate()}일 ${String(d.getHours()).padStart(
    2,
    '0',
  )}:${String(d.getMinutes()).padStart(2, '0')}`;
};

interface ShareConfirmModalProps {
  open: boolean;
  isSaving: boolean;
  channel: ShareChannel;
  seq: number | null;
  /**
   * 카카오톡 웹훅이 도착해 백엔드가 실제 전달을 확인한 시각.
   * 값이 있고 + 가장 마지막 공유 채널이 카카오톡인 경우 "전달 완료" 톤(확정형)으로,
   * 그 외에는 "잘 전달하셨나요?" 톤(추정형)으로 표시합니다.
   */
  kakaoSharedAt?: Date | null;
  /**
   * 백엔드가 기록한 마지막 공유 채널 (`GiftCoupon.lastSharedChannel` /
   * `GiftDelivery.lastSharedChannel`). KakaoTalk 일 때 카톡 확정 톤을 적용합니다.
   *
   * 쿠폰/배송이 별도 enum 이지만 string 값은 동일하므로 단순 string 비교로 처리합니다.
   */
  lastSharedChannel?: string | null;
  /**
   * 회수(잘못 보낸 케이스) 플로우를 지원하는 아이템 종류.
   * 백엔드 restricted-phone 엔드포인트가 쿠폰/배송 양쪽에 있어 현재는 분기 사용처가
   * 없지만, 향후 카피/UI 분기 가능성을 위해 prop 은 유지합니다.
   */
  itemKind?: 'coupon' | 'delivery';
  /**
   * 재전달 케이스에서 기존에 남겨진 메모. 있으면 메모 입력에 prefill 합니다.
   */
  existingMemo?: string;
  /**
   * 잠금된 수령자 휴대폰번호. 있으면 confirm 단계에서 부가 정보로 표시합니다.
   * (회수 플로우 또는 예방 잠금으로 set 되어 있을 수 있음)
   */
  additionalRecipientPhone?: string;
  onConfirm: (memo: string) => void;
  onDeny: () => void;
  onLater: () => void;
  onClose: () => void;
  /**
   * 카카오톡 확정 건에서 "잘못 보냄 → 번호 잠금 + 다시 전달" 회수 플로우 핸들러.
   * `memo` 는 받는 분 성함을 적게 받아서 백엔드 메모 필드에 같이 저장합니다.
   * 생략하면 회수 플로우가 비활성화됩니다.
   */
  onRecoverWithPhone?: (phoneNumber: string, memo?: string) => void;
  /**
   * 다건수신(같은 분께 또 보냈을 가능성) 충돌 목록.
   * 비어있지 않고 카카오 확정 건이면 메모 입력 대신 "같은 분께 또 보낸 것 같아요"
   * 안내 step 으로 분기한다. (사용자가 "잘 보낸 게 맞아요" 를 누르면 일반 메모 입력으로 fall-through)
   */
  duplicateConflicts?: DuplicateRecipientConflict[];
  /**
   * 다건수신 안내에서 "카톡 확인하러 가기" 클릭 시 호출.
   * 호출부는 localStorage 에 의심 마크를 저장하고 모달을 닫는 역할.
   */
  onInvestigate?: () => void;
}

export const ShareConfirmModal: FC<ShareConfirmModalProps> = ({
  open,
  isSaving,
  channel,
  seq,
  kakaoSharedAt,
  lastSharedChannel,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  itemKind = 'coupon',
  existingMemo,
  additionalRecipientPhone,
  onConfirm,
  onDeny,
  onLater,
  onClose,
  onRecoverWithPhone,
  duplicateConflicts,
  onInvestigate,
}) => {
  const { ShareKakao } = Icon();
  const [memo, setMemo] = useState('');
  const [step, setStep] = useState<'confirm' | 'retry'>('confirm');
  const [recoverPhone, setRecoverPhone] = useState('');
  const [recoverName, setRecoverName] = useState('');
  /**
   * 다건수신 안내에서 "잘 보낸 게 맞아요" 를 눌렀을 때 true 로 바꿔서
   * 동일 모달 안에서 일반 카톡 확정 메모 입력 step 으로 fall-through 한다.
   * 모달이 새로 열릴 때 항상 false 로 초기화.
   */
  const [duplicateAcknowledged, setDuplicateAcknowledged] = useState(false);

  // 카톡 확정 판단:
  //   - 백엔드 웹훅으로 kakaoSharedAt 가 찍혀 있고
  //   - 현재 세션 채널이 카톡이거나 / 가장 마지막 기록 채널이 카톡인 경우
  // (둘 중 하나라도 카톡이면 카톡으로 도달했다는 신호로 본다)
  const isKakaoConfirmed =
    !!kakaoSharedAt &&
    (channel === ShareChannel.KakaoTalk || lastSharedChannel === ShareChannel.KakaoTalk);
  // 백엔드에 쿠폰/배송 모두 restricted-phone 엔드포인트가 있어 itemKind 게이팅 제거.
  const canRecoverWithPhone = isKakaoConfirmed && !!onRecoverWithPhone;

  // 다건수신 안내 노출 조건: 카톡 확정 + 충돌 1건 이상 + 사용자가 아직 ack 안 함
  // + 회수 플로우 step 이 아님.
  const hasUnacknowledgedDuplicate =
    isKakaoConfirmed &&
    !duplicateAcknowledged &&
    !!duplicateConflicts &&
    duplicateConflicts.length > 0;

  // 모달이 새로 열릴 때마다 입력값 초기화 (다른 답례품으로 새로 떴을 때 대응).
  // 메모는 재전달 케이스에서 같은 분께 가는 경우가 많아 prefill 하지만,
  // 회수 플로우의 휴대폰번호는 (잠금된 번호가 잘못되어 회수에 들어왔을 가능성이 높아)
  // 자동 채우지 않고 별도 안내로만 표시한다.
  useEffect(() => {
    if (open) {
      setMemo(existingMemo ?? '');
      setRecoverPhone('');
      setRecoverName('');
      setStep('confirm');
      setDuplicateAcknowledged(false);
    }
  }, [open, existingMemo]);

  const handleConfirm = useCallback(() => {
    onConfirm(memo);
  }, [memo, onConfirm]);

  // 카카오 확정 변형에서 메모가 이미 있는 경우(재전달 등) 버튼/헤더를 단순화한다.
  // 변경 없음 → "확인", 수정함 → "메모 저장", 신규 입력 → "메모 남기기"
  const hasExistingMemo = !!existingMemo?.trim();
  const isMemoChanged = memo.trim() !== (existingMemo ?? '').trim();
  const memoButtonLabel = isSaving
    ? '저장 중...'
    : hasExistingMemo
    ? isMemoChanged
      ? '메모 저장'
      : '확인'
    : '메모 남기기';

  const handleGoRetry = useCallback(() => {
    setStep('retry');
  }, []);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleBackdropClick = useCallback(() => {
    if (isSaving) return;
    // confirm 단계: 닫기 = 나중에 (모달만 닫고 pendingConfirm 보존)
    // retry 단계: 닫기 = 그냥 닫기 (역시 보존)
    handleClose();
  }, [isSaving, handleClose]);

  const handleSubmitRecover = useCallback(() => {
    if (!onRecoverWithPhone) return;
    const phone = recoverPhone.replace(/[^0-9]/g, '');
    if (phone.length < 10) {
      window.alert('받으실 분의 휴대폰번호를 정확히 입력해 주세요.');
      return;
    }
    const memoName = recoverName.trim();
    onRecoverWithPhone(phone, memoName ? memoName : undefined);
  }, [onRecoverWithPhone, recoverPhone, recoverName]);

  const handleLegacyRetry = useCallback(() => {
    onDeny();
  }, [onDeny]);

  if (!open) return null;

  const channelName = CHANNEL_LABEL[channel];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6"
      onClick={handleBackdropClick}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {step === 'confirm' && hasUnacknowledgedDuplicate ? (
          // ===== 다건수신 의심 안내 (카톡 확정 + 같은 카톡방 반복 전송 감지) =====
          <DuplicateRecipientWarning
            seq={seq}
            conflicts={duplicateConflicts ?? []}
            isSaving={isSaving}
            onAcknowledge={() => setDuplicateAcknowledged(true)}
            onInvestigate={onInvestigate}
            onClose={handleClose}
          />
        ) : step === 'confirm' ? (
          isKakaoConfirmed ? (
            // ===== 카카오톡 확정 (백엔드 웹훅 확인됨) =====
            <>
              <div className="mb-3 flex items-start gap-3">
                <ShareKakao className="h-10 w-10 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-bold text-gray-800">전달 완료</h3>
                  {kakaoSharedAt && (
                    <p className="text-[11px] text-gray-500">
                      {formatSharedAt(kakaoSharedAt)} 카카오톡 확인
                    </p>
                  )}
                </div>
              </div>

              <p className="mb-4 break-keep text-sm leading-relaxed text-gray-700">
                {channel === ShareChannel.KakaoTalk ? (
                  seq ? (
                    <>
                      카카오톡으로{' '}
                      <span className="font-bold text-purple-700">#{seq}번째</span>{' '}
                      답례품을 잘 전달하였습니다.
                    </>
                  ) : (
                    <>카카오톡으로 답례품을 잘 전달하였습니다.</>
                  )
                ) : (
                  // 이번 세션은 SMS/다른앱 클릭이지만 직전 카톡 전송이 웹훅으로 확인된 케이스
                  <>
                    {seq ? (
                      <>
                        <span className="font-bold text-purple-700">#{seq}번째</span>{' '}
                        답례품
                      </>
                    ) : (
                      '답례품'
                    )}
                    이 카카오톡으로 전달된 것이 확인되었습니다.
                  </>
                )}
              </p>

              {additionalRecipientPhone && (
                <div className="mb-3 flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <LockClosedIcon className="h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
                  <span className="truncate">
                    <span className="font-bold">
                      {phoneNumberStringFormator(additionalRecipientPhone)}
                    </span>
                    <span className="ml-1 text-amber-600">로만 수령 가능</span>
                  </span>
                </div>
              )}

              <div className="mb-4">
                {hasExistingMemo ? (
                  <p className="mb-2 text-xs font-medium text-gray-500">메모</p>
                ) : (
                  <>
                    <p className="mb-1 break-keep text-sm font-semibold text-gray-800">
                      누구에게 보냈는지 메모해 두세요
                    </p>
                    <p className="mb-2 text-[11px] leading-relaxed text-gray-500">
                      메모를 남기시면 어느 분께 전해드렸는지 나중에 한눈에 확인할 수 있어요.
                    </p>
                  </>
                )}
                <input
                  type="text"
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  placeholder="예: 김민수 (삼촌)"
                  maxLength={30}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm placeholder:text-gray-300 focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-400"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={canRecoverWithPhone ? handleGoRetry : handleClose}
                  disabled={isSaving}
                  className="w-2/5 rounded-xl border border-gray-300 py-3 text-sm font-medium text-gray-600 active:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={isSaving}
                  className={`w-3/5 rounded-xl bg-purple-600 py-3 text-sm font-bold text-white active:bg-purple-700 ${
                    isSaving ? 'opacity-50' : ''
                  }`}
                >
                  {memoButtonLabel}
                </button>
              </div>
            </>
          ) : (
            // ===== SMS / 다른 앱 (전달 여부 추정형) =====
            <>
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100">
                  <CheckCircleIcon className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">잘 전달하셨나요?</h3>
                  {seq && (
                    <p className="text-xs font-bold text-purple-600">#{seq}번째 답례품</p>
                  )}
                </div>
              </div>

              <p className="mb-3 break-keep text-sm leading-relaxed text-gray-700">
                {channel === ShareChannel.SMS
                  ? `${channelName}로 `
                  : `${channelName}으로 `}
                {seq ? (
                  <span className="font-bold text-purple-700">#{seq}번째</span>
                ) : null}{' '}
                답례품을 잘 보내셨나요?
              </p>

              {additionalRecipientPhone && (
                <div className="mb-3 flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <LockClosedIcon className="h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
                  <span className="truncate">
                    <span className="font-bold">
                      {phoneNumberStringFormator(additionalRecipientPhone)}
                    </span>
                    <span className="ml-1 text-amber-600">로만 수령 가능</span>
                  </span>
                </div>
              )}

              <div className="mb-4">
                <p className="mb-2 break-keep text-sm font-bold leading-relaxed text-gray-800">
                  {seq ? `#${seq}번 ` : ''}누구에게 보냈는지 메모해 두세요
                </p>
                <p className="mb-2 text-xs leading-relaxed text-gray-500">
                  메모를 남기시면 나중에 전달 현황을 한눈에 파악할 수 있어요.
                </p>
                <input
                  type="text"
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  placeholder="예: 홍길동"
                  maxLength={30}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm placeholder:text-gray-300 focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-400"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleGoRetry}
                  disabled={isSaving}
                  className="w-2/5 rounded-xl border border-gray-300 py-3 text-sm font-medium text-gray-600 active:bg-gray-50"
                >
                  아직이요
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={isSaving}
                  className={`w-3/5 rounded-xl bg-purple-600 py-3 text-sm font-bold text-white active:bg-purple-700 ${
                    isSaving ? 'opacity-50' : ''
                  }`}
                >
                  {isSaving ? '확인 중...' : '네, 보냈어요'}
                </button>
              </div>
            </>
          )
        ) : canRecoverWithPhone ? (
          // ===== 카카오톡 확정 건의 회수(잘못 보냄) 플로우 =====
          <>
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                  <ShieldCheckIcon className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">혹시 잘못 보내셨나요?</h3>
              </div>
              <button
                onClick={handleClose}
                disabled={isSaving}
                className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 active:bg-gray-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            <p className="mb-3 break-keep text-base font-medium leading-relaxed text-gray-800">
              받으실 분 번호를 등록하시면 다시 보내실 수 있어요.
            </p>
            <p className="mb-3 break-keep text-sm leading-relaxed text-gray-600">
              수령 시 본인 인증을 거치므로 다른 분께 가있어도 안전합니다.
            </p>
            <p className="mb-4 break-keep text-sm font-medium leading-relaxed text-amber-700">
              잘못 보낸 카톡 메시지는 직접 삭제해 주세요.
            </p>

            <div className="mb-4 flex flex-col gap-2">
              <input
                type="tel"
                inputMode="numeric"
                value={phoneNumberStringFormator(recoverPhone)}
                onChange={(e) => setRecoverPhone(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="받으실 분 휴대폰번호"
                maxLength={13}
                className="w-full rounded-lg border border-amber-300 px-3 py-3 text-base focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
              />
              <input
                type="text"
                value={recoverName}
                onChange={(e) => setRecoverName(e.target.value)}
                placeholder="성함 (선택)"
                maxLength={30}
                className="w-full rounded-lg border border-amber-300 px-3 py-3 text-base focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleClose}
                disabled={isSaving}
                className="w-2/5 rounded-xl border border-gray-300 py-3 text-sm font-medium text-gray-600 active:bg-gray-50"
              >
                닫기
              </button>
              <button
                onClick={handleSubmitRecover}
                disabled={isSaving || recoverPhone.length < 10}
                className={`w-3/5 rounded-xl bg-amber-600 py-3 text-sm font-bold text-white active:bg-amber-700 ${
                  isSaving || recoverPhone.length < 10 ? 'opacity-50' : ''
                }`}
              >
                {isSaving ? '저장 중...' : '번호 등록 후 다시 전달'}
              </button>
            </div>
          </>
        ) : (
          // ===== SMS/다른앱 또는 회수 미지원: 기존 "다시 보내볼까요?" 플로우 =====
          <>
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                  <RefreshIcon className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">다시 보내볼까요?</h3>
              </div>
              <button
                onClick={handleClose}
                disabled={isSaving}
                className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 active:bg-gray-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            <p className="mb-1.5 break-keep text-sm leading-relaxed text-gray-700">
              {channelName}에서 보내실 분을 못 찾으셨나요?
            </p>
            <p className="mb-5 break-keep text-sm leading-relaxed text-gray-700">
              문자나 다른 방법으로도 전달할 수 있어요.
            </p>

            <div className="flex gap-2">
              <button
                onClick={onLater}
                disabled={isSaving}
                className="w-2/5 rounded-xl border border-gray-300 py-3 text-sm font-medium text-gray-600 active:bg-gray-50"
              >
                나중에 할게요
              </button>
              <button
                onClick={handleLegacyRetry}
                disabled={isSaving}
                className={`w-3/5 rounded-xl bg-purple-600 py-3 text-sm font-bold text-white active:bg-purple-700 ${
                  isSaving ? 'opacity-50' : ''
                }`}
              >
                {isSaving ? '확인 중...' : '다른 방법으로 보내기'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================
// 다건수신 의심 안내 (ShareConfirmModal 내부에서만 사용)
// ============================================================

interface DuplicateRecipientWarningProps {
  seq: number | null;
  conflicts: DuplicateRecipientConflict[];
  isSaving: boolean;
  /** "잘 보낸 게 맞아요" → 같은 모달 안에서 일반 메모 입력으로 fall-through */
  onAcknowledge: () => void;
  /** "카톡 확인하러 가기" → 의심 마크 저장 + 모달 닫기 */
  onInvestigate?: () => void;
  /** X 버튼 (마크 저장 없이 단순 닫기 — 다음 진입 시 다시 노출됨) */
  onClose: () => void;
}

const DuplicateRecipientWarning: FC<DuplicateRecipientWarningProps> = ({
  seq,
  conflicts,
  isSaving,
  onAcknowledge,
  onInvestigate,
  onClose,
}) => {
  const sortedConflicts = [...conflicts].sort((a, b) => a.seq - b.seq);
  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
            <ExclamationIcon className="h-6 w-6 text-red-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-800">
            같은 분께 또 전달된 것 같아요
          </h3>
        </div>
        <button
          onClick={onClose}
          disabled={isSaving}
          className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 active:bg-gray-200"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div className="mb-4 break-keep text-sm leading-relaxed text-gray-700">
        <p className="mb-2 flex flex-wrap items-center gap-1.5">
          {seq != null && <SeqChip seq={seq} />}
          <span className="text-base font-bold text-gray-800">답례품</span>
          <span>을</span>
        </p>
        <p className="flex flex-wrap items-center gap-1.5">
          {sortedConflicts.map((c) => (
            <SeqChip key={c.code} seq={c.seq} />
          ))}
          <span className="text-base font-bold text-gray-800">답례품</span>
          <span>을 받으신 분께 또 보내신 것 같아요.</span>
        </p>
      </div>

      <p className="mb-3 break-keep text-base leading-relaxed text-gray-700">
        <span className="font-bold text-gray-900">카톡방</span>에 가셔서{' '}
        <span className="font-bold text-gray-900">기존에 보내신 게</span> 있는지{' '}
        <span className="font-bold text-gray-900">확인</span>해 주세요.
      </p>
      <p className="mb-5 break-keep text-sm leading-relaxed text-gray-600">
        실수로 보낸 게 있다면 최근 메시지를{' '}
        <span className="font-bold text-red-600">꾹 눌러서 삭제</span>해 주세요.
      </p>

      <div className="flex gap-2">
        <button
          onClick={onAcknowledge}
          disabled={isSaving}
          className="w-1/2 rounded-xl border border-gray-300 py-3 text-sm font-medium text-gray-600 active:bg-gray-50"
        >
          잘 보낸 게 맞아요
        </button>
        <button
          onClick={onInvestigate}
          disabled={isSaving || !onInvestigate}
          className={`w-1/2 rounded-xl bg-purple-600 py-3 text-sm font-bold text-white active:bg-purple-700 ${
            isSaving || !onInvestigate ? 'opacity-50' : ''
          }`}
        >
          카톡 확인하러 가기
        </button>
      </div>
    </>
  );
};

